import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { THEME_PREFS, useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { IconMonitor, IconMoon, IconSun } from './icons';

const ICON = { system: IconMonitor, light: IconSun, dark: IconMoon } as const;

/** System / Light / Dark segmented control built on native radios (arrow keys work out of the box). */
export function ThemeSwitch({ className, showLabels = false }: { className?: string; showLabels?: boolean }) {
  const { t } = useTranslation();
  const { pref, setPref } = useTheme();
  const name = useId();
  return (
    <fieldset
      className={cn(
        'm-0 inline-flex items-center gap-0.5 rounded-lg border border-border bg-bg p-0.5',
        className,
      )}
    >
      <legend className="sr-only">{t('theme.label')}</legend>
      {THEME_PREFS.map((p) => {
        const Icon = ICON[p];
        return (
          <label
            key={p}
            title={t(`theme.${p}`)}
            className={cn(
              'inline-flex h-6 cursor-pointer items-center justify-center gap-1.5 rounded-md px-2 text-xs text-muted transition-colors hover:text-fg',
              'has-[:checked]:bg-panel-2 has-[:checked]:text-fg has-[:checked]:shadow-soft',
              'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-[color-mix(in_srgb,var(--accent)_40%,transparent)]',
            )}
          >
            <input
              type="radio"
              name={name}
              value={p}
              checked={pref === p}
              onChange={() => setPref(p)}
              className="sr-only"
              aria-label={showLabels ? undefined : t(`theme.${p}`)}
            />
            <Icon size={14} />
            {showLabels ? <span>{t(`theme.${p}`)}</span> : null}
          </label>
        );
      })}
    </fieldset>
  );
}
